import { blue, grey } from "@/utils/Colors";
import {
  alpha,
  Button as MuiButton,
  SxProps,
  Theme,
  Typography,
  TypographyProps,
} from "@mui/material";
import { MouseEventHandler } from "react";

interface ButtonProps {
  children?: React.ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  variant: "primary" | "secondary" | "tertiary";
  type?: "button" | "submit" | "reset";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  id?: string;
  onMouseOver?: MouseEventHandler<HTMLButtonElement>;
  color?: string;
  sx?: SxProps<Theme> | undefined;
  slotProps?: { typography?: Partial<TypographyProps> } | undefined;
}

export default function Button(props: Readonly<ButtonProps>) {
  // TODO: style disabled and smalls SAAS-820
  if (props.variant === "secondary") {
    return (
      <MuiButton
        id={props.id}
        type={props.type}
        variant="outlined"
        onClick={(event) => {
          if (props.onClick !== undefined) {
            props.onClick(event);
          }
        }}
        onMouseOver={(event) => {
          if (props.onMouseOver !== undefined) {
            props.onMouseOver(event);
          }
        }}
        disabled={props.disabled}
        endIcon={props.endIcon}
        startIcon={props.startIcon}
        size={props.size}
        fullWidth={props.fullWidth}
        sx={{
          // backgroundColor: "white",
          // TODO: transparant background?
          color: props.color ? props.color : blue[600],
          padding: 1,
          gap: 0.5,
          borderRadius: 1,
          border: "1px solid",
          borderColor: props.color ? alpha(props.color, 0.6) : blue[200],
          "&:hover": {
            backgroundColor: props.color ? alpha(props.color, 0.6) : blue[200],
            border: "1.5px",
            borderColor: props.color ? alpha(props.color, 0.8) : blue[400],
          },
          ...(!!props.sx && props.sx),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            textTransform: "none",
            color: props.color ? props.color : blue[600],
            ...(props.disabled && {
              color: grey[600],
            }),
            ...(!!props.slotProps?.typography?.sx &&
              props.slotProps.typography.sx),
          }}
        >
          {props.children}
        </Typography>
      </MuiButton>
    );
  } else if (props.variant === "tertiary") {
    return (
      <MuiButton
        id={props.id}
        type={props.type}
        variant="text"
        onClick={(event) => {
          if (props.onClick !== undefined) {
            props.onClick(event);
          }
        }}
        onMouseOver={(event) => {
          if (props.onMouseOver !== undefined) {
            props.onMouseOver(event);
          }
        }}
        disabled={props.disabled}
        endIcon={props.endIcon}
        startIcon={props.startIcon}
        size={props.size}
        fullWidth={props.fullWidth}
        sx={{
          backgroundColor: "white",
          color: props.color ? props.color : blue[700],
          padding: 1,
          gap: 0.5,
          borderRadius: 1,
          border: "none",
          "&:hover": {
            backgroundColor: props.color ? alpha(props.color, 0.6) : blue[200],
          },
          ...(!!props.sx && props.sx),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            textTransform: "none",
            color: props.color ? props.color : blue[700],
            ...(props.disabled && {
              color: grey[600],
            }),
            ...(!!props.slotProps?.typography?.sx &&
              props.slotProps.typography.sx),
          }}
        >
          {props.children}
        </Typography>
      </MuiButton>
    );
  } else {
    return (
      <MuiButton
        id={props.id}
        type={props.type}
        variant="contained"
        onClick={(event) => {
          if (props.onClick !== undefined) {
            props.onClick(event);
          }
        }}
        onMouseOver={(event) => {
          if (props.onMouseOver !== undefined) {
            props.onMouseOver(event);
          }
        }}
        disabled={props.disabled}
        endIcon={props.endIcon}
        startIcon={props.startIcon}
        size={props.size}
        fullWidth={props.fullWidth}
        sx={{
          backgroundColor: props.color ? props.color : blue[700],
          color: props.color ? alpha(props.color, 0.2) : blue[100],
          padding: 1,
          gap: 0.5,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: props.color ? props.color : blue[800],
          },
          ...(!!props.sx && props.sx),
        }}
      >
        <Typography
          variant="body2"
          sx={{
            textTransform: "none",
            color: blue[100],
            ...(props.disabled && {
              color: grey[600],
            }),
            ...(!!props.slotProps?.typography?.sx &&
              props.slotProps.typography.sx),
          }}
        >
          {props.children}
        </Typography>
      </MuiButton>
    );
  }
}
